import { useState } from "react";
import { callFunction } from "../api/callFunction";
import { toast } from "react-toastify";
import {
  ChartBarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CurrencyDollarIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-05-03" -> "May 2026" (string-sliced to dodge timezone off-by-one).
function formatMonth(dateStr) {
  if (typeof dateStr !== "string") return "";
  const [y, m] = dateStr.split("-");
  const idx = Number(m) - 1;
  return MONTHS[idx] ? `${MONTHS[idx]} ${y}` : "";
}

// Firestore COSINE distance -> a friendly match %. similarity = 1 - distance.
function matchPct(distance) {
  if (typeof distance !== "number") return null;
  return Math.max(0, Math.min(100, Math.round((1 - distance) * 100)));
}

/**
 * "Similar past events" panel for a suggestion card. Lazy-loads the comparables
 * (vector KNN over real past events) on first expand via the getEventComparables
 * callable, so the grid doesn't fire a lookup per card on render. SuperAdmins get
 * revenue back; for everyone else the server strips it, so we just render what's there.
 */
export default function SimilarEventsPanel({ suggestion }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [comparables, setComparables] = useState([]);
  const [stats, setStats] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await callFunction("getEventComparables", { suggestionId: suggestion.id }, { timeout: 60000 });
      setComparables(res?.comparables || []);
      setStats(res?.stats || null);
      setLoaded(true);
    } catch (err) {
      console.error("getEventComparables failed:", err);
      toast.error(err?.message || "Failed to load similar events");
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded && !loading) load();
  };

  const hasRevenue = stats?.medianRevenue != null;

  return (
    <div className="border-t border-gray-100 dark:border-gray-800 pt-2 mb-2">
      <button
        onClick={toggle}
        className="flex w-full items-center justify-between text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      >
        <span className="flex items-center gap-1.5">
          <Squares2X2Icon className="h-3.5 w-3.5 shrink-0" />
          Similar past events
          {loaded && (
            <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 dark:text-gray-300">
              {comparables.length}
            </span>
          )}
        </span>
        {open ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
      </button>

      {open && (
        <div className="mt-2">
          {loading && (
            <div className="flex items-center gap-2 py-3 text-xs text-gray-400 dark:text-gray-500">
              <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-indigo-500" />
              Finding events like this…
            </div>
          )}

          {!loading && loaded && comparables.length === 0 && (
            <p className="py-2 text-xs text-gray-400 dark:text-gray-500">
              No similar past events found yet.
            </p>
          )}

          {!loading && comparables.length > 0 && (
            <div className="rounded-lg border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/50 dark:bg-indigo-900/15 px-2.5 py-2">
              {stats && (
                <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-indigo-700/90 dark:text-indigo-300/90">
                  <span className="flex items-center gap-1 font-semibold">
                    <ChartBarIcon className="h-3.5 w-3.5 shrink-0" />
                    median {stats.medianAttendance ?? "—"} checked in
                  </span>
                  {hasRevenue && (
                    <span className="flex items-center gap-1">
                      <CurrencyDollarIcon className="h-3.5 w-3.5 shrink-0" />
                      median ${Math.round(stats.medianRevenue).toLocaleString("en-US")}
                    </span>
                  )}
                </div>
              )}

              <ul className="divide-y divide-indigo-100/70 dark:divide-indigo-900/30">
                {comparables.map((c) => {
                  const pct = matchPct(c.distance);
                  const meta = [c.activity, c.area, c.slot].filter(Boolean).join(" · ");
                  const month = formatMonth(c.date);
                  return (
                    <li key={c.id} className="py-1.5 first:pt-0 last:pb-0">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate" title={c.name}>
                          {c.name}
                        </span>
                        {pct != null && (
                          <span className="shrink-0 rounded-full bg-indigo-100 dark:bg-indigo-800/50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700 dark:text-indigo-200">
                            {pct}% match
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                        {meta && <span className="truncate">{meta}</span>}
                        {month && <span>· {month}</span>}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-gray-600 dark:text-gray-300">
                        <span className="flex items-center gap-1">
                          <ChartBarIcon className="h-3 w-3 shrink-0 text-gray-400" />
                          {c.attendance} checked in
                        </span>
                        {c.revenue != null && (
                          <span className="flex items-center gap-1">
                            <CurrencyDollarIcon className="h-3 w-3 shrink-0 text-gray-400" />
                            ${Math.round(c.revenue).toLocaleString("en-US")}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
